import { useCallback, useEffect, useRef, useState } from 'react'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import { NumberFormField, Range } from '../../../../components/inputs'
import { now } from '../../../../utils/date-service'
import { Button } from '../../../../components/button'
import { Mobile } from '../../../../components/media'

export default function YearMileageFilter({
  year: defaultYear,
  mileage: defaultMileage,
  maxMileage,
  errors,
  onYearChange,
  onMileageChange,
  handleFilterToggle,
}) {
  const [year, setYear] = useState({ start: defaultYear.start, end: defaultYear.end })
  const [mileage, setMileage] = useState({ start: defaultMileage.start, end: defaultMileage.end })
  const hydratedRef = useRef()

  const handleDebouncedYearChange = useDebouncedCallback(onYearChange, 500, [onYearChange])

  useEffect(() => {
    if (hydratedRef.current) {
      handleDebouncedYearChange(year)
    }
  }, [handleDebouncedYearChange, year])

  const handleYearChange = useCallback((event) => {
    const { name, value } = event.target
    setYear((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleYearBlur = useCallback(() => {
    setYear((prev) => (prev.start > prev.end ? { start: prev.start, end: prev.start } : prev))
  }, [])

  const handleYearRangeChange = useCallback(([start, end]) => {
    setYear({ start: start.toString(), end: end.toString() })
  }, [])

  const handleDebouncedMileageChange = useDebouncedCallback(onMileageChange, 500, [onMileageChange])

  useEffect(() => {
    if (hydratedRef.current) {
      handleDebouncedMileageChange(mileage)
    }
  }, [handleDebouncedMileageChange, mileage])

  useEffect(() => {
    hydratedRef.current = true
  }, [])

  const handleMileageChange = useCallback((event) => {
    const { name, value } = event.target
    let cleanedNumber = value.replace(/[^0-9]/g, '')
    setMileage((prev) => ({ ...prev, [name]: cleanedNumber }))
  }, [])

  const handleMileageBlur = useCallback(() => {
    setMileage((prev) => (prev.start > prev.end ? { start: prev.start, end: prev.start } : prev))
  }, [])

  const handleMileageRangeChange = useCallback(([start, end]) => {
    setMileage({ start: start.toString(), end: end.toString() })
  }, [])

  return (
    <div className='wrapper'>
      <p className='filterLabel year'>Year Range</p>
      <div className='inputsContainer flex alignCenter'>
        <NumberFormField
          className='year rangeStart'
          name='start'
          value={year.start}
          onChange={handleYearChange}
          onBlur={handleYearBlur}
          error={errors.yearStart}
        />
        <span>to</span>
        <NumberFormField
          className='year rangeEnd'
          name='end'
          value={year.end}
          onChange={handleYearChange}
          onBlur={handleYearBlur}
          error={errors.yearEnd}
        />
      </div>
      <div className='rangeContainer'>
        <Range
          pearling
          min={0}
          max={now().getFullYear() + 1}
          onChange={handleYearRangeChange}
          value={[Math.min(year.start, year.end), Math.max(year.start, year.end)]}
        />
        <div className='rangeValues year flex justifyBetween'>
          <span>{year.start}</span>
          <span>{year.end}</span>
        </div>
      </div>

      <p className='filterLabel mileage'>Mileage</p>
      <div className='inputsContainer flex alignCenter'>
        <NumberFormField
          className='mileage rangeStart'
          name='start'
          separateGrands
          value={mileage.start}
          onChange={handleMileageChange}
          onBlur={handleMileageBlur}
          error={errors.mileageStart}
        />
        <span>to</span>
        <NumberFormField
          className='mileage rangeEnd'
          name='end'
          separateGrands
          value={mileage.end}
          onChange={handleMileageChange}
          onBlur={handleMileageBlur}
          error={errors.mileageEnd}
        />
      </div>
      <div className='rangeContainer'>
        <Range
          pearling
          min={0}
          step={100}
          max={maxMileage}
          onChange={handleMileageRangeChange}
          value={[Math.min(mileage.start, mileage.end), Math.max(mileage.start, mileage.end)]}
        />
        <div className='rangeValues mileage flex justifyBetween'>
          <span>{Number(mileage.start).toLocaleString()}</span>
          <span>{Number(mileage.end).toLocaleString()}</span>
          <span>{maxMileage.toLocaleString()}</span>
        </div>
      </div>
      <Mobile>
        <div className='applyButton'>
          <Button onClick={() => handleFilterToggle('yearMileage')}>Apply</Button>
        </div>
      </Mobile>

      <style jsx>{`
        .applyButton {
          position: absolute;
          left: calc(50% - 46.9px);
          margin-top: 5rem;
        }
        .wrapper {
          height: 36rem;
          padding: 2rem;
          border-radius: 1rem;
          background: #fff;
          box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
          color: var(--secondary-text-color);
        }

        .filterLabel {
          font-size: 1.6rem;
          font-weight: 700;

          &.mileage {
            margin: 1.7rem 0 0;
            padding: 1.7rem 0 0;
            border-top: 0.1rem solid var(--separator-line-color);
          }
        }

        .inputsContainer {
          margin: 1.1rem 0 3.2rem;

          > span {
            margin: 0 1.6rem;
            font-size: 1.6rem;
          }

          :global(.inputContainer) {
            width: 100%;
            color: var(--primary-text-color);
          }
        }

        .rangeValues {
          margin: 0.5rem 0 0;
          font-size: 1.2rem;
        }

        @media (max-width: 1024px) {
          .wrapper {
            max-width: 100%;
            height: auto;
            padding: 3rem;
            box-shadow: none;
            border-radius: 0;
          }

          .filterLabel {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  )
}
